<template lang="pug">
	.web-service--outputs.flex-col.space-large
		.flex-row.space-medium.align-center|
			.font-big.upper.grey {{$tCaption('Outputs')}}
			DxButton(icon="plus", @click="addOutput")
			.flex-row.space.align-center
				DxButton(:text="$tCaption('Calculate outputs')", @click="calculateOutputs()", :disabled="isCalculatingResults")
				Loader(v-if="isCalculatingResults", text="")
		.flex-col.space.outputs.align-start
			.output.flex-row.space.full-width.wrap.padded-children(v-for="(output, pIdx) in service.JsonDefinition.Outputs")
				DxButton(icon="trash", stylingMode="text", type="danger" @click="service.JsonDefinition.Outputs.splice(pIdx, 1)")
				.flex-col
					.font-small.upper.grey {{$tCaption('Name')}}
					DxTextBox(:value.sync="output.Name")
				.flex-col
					.font-small.upper.grey {{$tCaption('Description')}}
					DxTextBox(:value.sync="output.Description")
				.flex-col(v-if="output.OutputEvalType === 0")
					.font-small.upper.grey {{$tCaption('Script to retrieve value')}}
					DxButton(:text="$tCaption('Edit script')", stylingMode="text", type="danger", @click="v => editOutputScript(output)")
				.flex-col(v-else)
					.font-small.upper.grey {{$tCaption('Path to retrieve value')}}
					DxTextBox(:value.sync="output.Path")
				.flex-col
					.font-small.upper.grey {{$tCaption('Eval type')}}
					DxSelectBox(:items="outputEvalTypes", value-expr="id", display-expr="name", :search-enabled="false", :value.sync="output.OutputEvalType", :width="90")
				.flex-col
					.font-small.upper.grey {{$tCaption('Data type')}}
					DxSelectBox(:items="outputDataTypes", value-expr="id", display-expr="name", :search-enabled="false", :value.sync="output.DataType", :width="90")
				.flex-col(v-if="formats[output.DataType]")
					.font-small.upper.grey {{$tCaption('Format')}}
					.flex-row.align-center
						DxTextBox(:value="output.Format", @value-changed="e => $set(output, 'Format', e.value)", :width="120", valueChangeEvent="change")
						DxDropDownButton.iconed(:data-source="formats[output.DataType]", :width="30", icon="", stylingMode="text", :useItemTextAsTitle="false", :dropDownOptions="dropDownOptions", 
							:onItemClick="e => $set(output, 'Format', e.itemData)")
				.flex-col
					.font-small.upper.grey {{$tCaption('Default speech value')}}
					DxTextBox(:value="output.DefaultSpeechValue", @value-changed="e => $set(output, 'DefaultSpeechValue', e.value)")
				template(v-if="output.IsArray && output.ArrayFields")
					.flex-col
						.font-small.upper.grey {{$tCaption('Key value')}}
						DxSelectBox(:items="output.ArrayFields", value-expr="id", display-expr="id", :search-enabled="false", :dropDownOptions="dropDownOptions", :value="output.KeyField", :placeholder="output.KeyField || ''",
							@item-click="e => $set(output, 'KeyField', e.itemData.id)", :width="90")
					.flex-col
						.font-small.upper.grey {{$tCaption('Display value')}}
						DxSelectBox(:items="output.ArrayFields", value-expr="id", display-expr="id", :search-enabled="false", :dropDownOptions="dropDownOptions", :value="output.DisplayField", :placeholder="output.DisplayField || ''",
							@item-click="e => $set(output, 'DisplayField', e.itemData.id)", :width="90")
					.flex-col
						DxButton(:text="$tCaption('Columns list')", @click="editOutputColumns(output)")
				.flex-col
					.font-small.upper.grey {{$tCaption('Status')}}
					DxSelectBox(:items="outputStatuses", value-expr="id", display-expr="name", :search-enabled="false", :dropDownOptions="dropDownOptions", :value="output.Status || 0", 
						@item-click="e => $set(output, 'Status', e.itemData.id)", :width="90")
				.flex-col(v-if="!output.IsArray && (output.DataType === 'list' || output.DataType === 'range')")
					.flex-row.space.align-center
						.font-small.upper.grey {{$tCaption('List options')}} ({{output.ListOptions.length}})
						a(@click="addListOption(output.ListOptions, `output_${pIdx}`)")
							i.fas.fa-plus
					DxScrollView.scroll-view(:ref="`output_${pIdx}`", showScrollbar="always", direction="vertical")
						.list-options.flex-col.space
							.flex-row.center.space(v-for="(lo, loIdx) in output.ListOptions")
								DxButton(icon="trash", stylingMode="text", type="danger" @click="output.ListOptions.splice(loIdx, 1)")
								DxTextBox(:value.sync="lo.Key", :placeholder="output.DataType === 'list' ? $tCaption('Key') : $tCaption('Range from')")
								DxTextBox(v-if="output.DataType === 'range'", :value="lo.KeyTo", :placeholder="output.DataType === 'list' ? $tCaption('Key') : $tCaption('Range to')", @value-changed="e => $set(lo, 'KeyTo', e.value)")
								DxTextBox(:value.sync="lo.Value", :placeholder="lo.Key ? `${$tCaption('Value')}: ${lo.Key}` : $tCaption('Value')")|
				.flex-col
					OutputSsmlTemplate(:output="output", :service="service")
				.flex-col
					OutputAIGenerator(:output="output", :service="service")
		Popup(v-if="editingOutput && editingOutput.output", :popupVisible="editingOutput.popup", :closeOnOutsideClick="false", :title="$tCaption('OUTPUT_NAME script').replace(/OUTPUT_NAME/g, editingOutput.output.Name || $tCaption('Output'))")
			DxTextArea(v-if="editingOutput.output", ref="outputScriptArea" :value.sync="editingOutput.script", :height="340", width="100%", class="script-area", valueChangeEvent="input", stylingMode="filled")
			.actions-description.flex-col.space(v-if="editingOutput && editingOutput.output && !editingOutput.script")
				span - {{$tCaption('Use $ to get access to result values. Example: return $.dataResult.value;')}}
				span - {{$tCaption('Use \'@ParameterName\' to get access to parameter values. Example: return @ParameterName/5;')}}
				span - {{$tCaption('Get access to available parameters/code snippets by right mouse click.')}}
			DxButton(slot="actions-left", :text="$tCaption('Script builder')", @click="showScriptBuilder")
			DxButton(slot="actions-right", :text="$tCaption('Cancel')", @click="editingOutput.popup = false")
			DxButton(slot="actions-right", :text="$tCaption('Save without check')", type="default", @click="saveEditingScript(true)", :disabled="isSavingOutputScript")
			DxButton(slot="actions-right", :text="$tCaption('Check & Save')", type="success", @click="saveEditingScript()", :disabled="isSavingOutputScript")
			DxContextMenu(:dataSource="outputParametersContextMenuItems", target=".script-area", @item-click="onOutputContextMenuItemClick")
		Popup(v-if="outputResultsPopupVisible", :popupVisible="outputResultsPopupVisible", :closeOnOutsideClick="false", :title="$tCaption('Results')")
			.flex-col.space.web-service--outputs(v-if="outputResults && outputResults.length")
				table
					tr(v-for="res in outputResults")
						td.output-result
							.flex-row.space.align-center.justify-end(:class="[res.Error ? 'error-colored' : 'success-colored']")
								.flex-row(:class="[res.Error ? 'error' : 'success']")
									fas(:icon="res.Error ? 'times' : 'check'")
								.flex-row.space.align-center
									span {{res.Output && res.Output.Name || res.Output}}:
									.adt-button.rounded.padded.clickable.hoverable.flex-row.center.align-center(v-if="res.Value && res.Value != res.DisplayValue", 
										:title="$tCaption('Original value')", @click="$notifyMessage(JSON.stringify(res.Value, void 0, '    '), { fullScreen: true })")
										fas(icon="magnifying-glass", size="2xs")
						td(:style="{ width: '100%' }")
							.flex-col.space-large.grey
								JsonViewer(:value="res.DisplayValue || res.Value")
								.flex-col(v-if="res.OriginDisplayValue")
									.font-mini.upper {{$tCaption('Original value')}}
									JsonViewer(:value="res.OriginDisplayValue")
								.font-tiny.error-colored(v-if="res.Error") {{res.Error}}
			DxButton(slot="actions-right", :text="$tCaption('Close')", @click="outputResultsPopupVisible = false")
		Popup(v-if="editingOutput && editingOutput.output", :popupVisible="editingOutput.columnsPopupVisible", :width="640", :closeOnOutsideClick="false", 
			:title="$tCaption('OUTPUT_NAME columns').replace(/OUTPUT_NAME/g, editingOutput.output.Name || $tCaption('Output'))")
			table
				thead
					th.text-start {{$tCaption('Id')}}
					th.text-start {{$tCaption('Caption')}}
					th.text-start {{$tCaption('Data type')}}
					th.text-start {{$tCaption('Visibility')}}
				tbody
					tr(v-for="col in editingOutput.output.ArrayFields")
						td 
							span {{col.id}}
						td
							DxTextBox(:value="col.caption", @value-changed="e => $set(col, 'caption', e.value)")
						td 
							DxSelectBox(:items="availableColumnDataTypes", :value.sync="col.dataType")
						td.text-center
							DxCheckBox(:value="col.visible !== false", @value-changed="e => $set(col, 'visible', e.value)")
			template(slot="actions-right")
				DxButton(:text="$tCaption('Close')", @click="editingOutput.columnsPopupVisible = false")
		ScriptBuilderPopup(ref="scriptBuilder", :service="service", :params="apiParams", @save="onScriptBuilderSave")
</template>

<script>
	import _ from 'lodash';
	import DxButton from 'devextreme-vue/button';
	import DxTextArea from 'devextreme-vue/text-area';	
	import Popup from 'app-core/Popup.vue';
	import DxContextMenu from 'devextreme-vue/context-menu';
	import DxSelectBox from 'devextreme-vue/select-box';
	import DxTextBox from 'devextreme-vue/text-box';
	import DxNumberBox from 'devextreme-vue/number-box';
	import { DxCheckBox } from 'devextreme-vue/check-box';
	import DxScrollView from 'devextreme-vue/scroll-view';
	import Loader from 'app-core/Loader.vue';
	import DxDropDownButton from 'devextreme-vue/drop-down-button';
	import OutputSsmlTemplate from './OutputSsmlTemplate.vue';
	import OutputAIGenerator from './OutputAIGenerator.vue';
	import ScriptBuilderPopup from './ScriptBuilderPopup.vue';
	import JsonViewer from 'vue-json-viewer';
	import { OUTPUT_STATUS } from 'app-core/constants';

	export default {
		translationCategory: 'webServiceCaller.Outputs',
		name: 'web-service--outputs',
		components: { DxButton, DxTextArea, Popup, DxSelectBox, DxContextMenu, DxTextBox, DxNumberBox, DxCheckBox, DxScrollView, Loader, DxDropDownButton, OutputSsmlTemplate, 
			OutputAIGenerator, JsonViewer, ScriptBuilderPopup },
		props: ['service', 'outputDataTypes', 'outputEvalTypes', 'apiResponse', 'apiParams', 'originalResponse'],
		data() {
			return {
				editingOutput: {
					popup: false,
					output: null,
					script: null,
					columnsPopupVisible: false
				},
				formattedResponse: null,
				isCalculatingResults: false,
				outputResults: [],
				outputResultsPopupVisible: false,
				isSavingOutputScript: false,
				formats: {
					date: ['dd.MM.yyyy', 'yyyy.MM.dd', 'dd/MM/yyyy'],
					number: ['0.##', '0.00', '### ### ### ##0.##'],
				},
				dropDownOptions: {
					minWidth: 200
				},
				availableColumnDataTypes: ['number', 'boolean', 'date', 'string'],
				outputStatuses: OUTPUT_STATUS.toLocale('name', this)
			};
		},
		computed: {
			outputParametersContextMenuItems() {
				return [
					{ 
						text: this.$tCaption('Insert parameter'),
						prevent: true,
						items: (this.service?.JsonDefinition?.Parameters || []).filter(f => f.Name).map(f => ({ id: f.Name, text: `@${f.Name}`, parameter: f }))
					},
					...this.codeSnippets
				]
			},
			codeSnippets() {
				return [
					{
						text: this.$tCaption('Code snippets'),
						prevent: true,
						items: [
							{
								id: 'UnixToDateHHMM',
								text: 'Unix date to GMT with offset (HH:MM)',
								codeSnippet: 'var date = new Date(<PASTE_HERE_DATE_IN_UNIX_FORMAT> * 1000 + <PASTE_HERE_TIMEZONE_IN_UNIX_FORMAT> * 1000);'
									+ '\r\nreturn `${date.getUTCHours()}:${date.getUTCMinutes()}'
							}
						]
					}
				]
			},
			scriptBuilderInfo() {
				return this.editingOutput?.output?.scriptBuilderInfo;
			}
		},
		mounted() {
			if (__DEBUG__)
				window.wbsOutputs = this;
		},
		methods: {
			onScriptBuilderSave(script, info) {
				this.editingOutput.script = script;
				this.$set(this.editingOutput.output, 'scriptBuilderInfo', info);
			},
			showScriptBuilder() {
				this.$refs.scriptBuilder.show(this.apiResponse, this.scriptBuilderInfo);
			},
			onVisibleColumnsItemClick(output, e) {
				if (!output.VisibleColumns) { 
					this.$set(output, 'VisibleColumns', []);
				}
				this.$Data.toggleSelectedIdInArray(e.itemData.id, output.VisibleColumns);
			},
			getValueType(val) {
				if (!`${val}`.replace(/[0-9.,]/g, '').replace(/[+-]/, '') && !isNaN(parseFloat(val))) 
					return 'number';
				if (['true', 'false'].includes(`${val}`.toLowerCase()))
					return 'boolean';
				if (val?.getDate || (`${val}`.length > 6 && this.$moment(val).isValid()))
					return 'date';
				if (typeof(val) === 'object')
					return 'object';
				return 'string';
			},
			async saveEditingScript(preventCheck) {
				try {
					this.isSavingOutputScript = true;
					const save = () => {
						this.editingOutput.output.Path = this.editingOutput.script;
						this.editingOutput.popup = false;
					};
					const testOutput = _.cloneDeep(this.editingOutput.output);
					testOutput.Path = this.editingOutput.script;

					let result = null;
					if (!preventCheck) {
						const calculatedOutput = await this.$Data.postExternalApi('ExternalService/CalculateOutputs', {
							ExternalServiceOid: this.service?.Oid,
							Parameters: this.apiParams,
							Outputs: [testOutput],
							JsonStringData: JSON.stringify(this.apiResponse?.__calculateOutputsResult__ || this.apiResponse?.__calculateOutputsResult || this.apiResponse),
						}, { dataType: 'text' }).then(r => this.getJson(r)).catch(ex => {
							if (__DEBUG__)
								console.error(ex);
						});

						result = calculatedOutput?.Data?.OutputResults[0];
					}

					if (result?.Error) {
						this.$confirmMessage({
							title: () => this.$tCaption('Confirm action', '@actions'),
							text: result?.Error,
							buttons: [{
								text: this.$tCaption('Edit script'),
								type: 'success'
							}, {
								text: this.$tCaption('Save anyway'),
								type: 'danger',
								onClick: () => save()
							}]
						});
					} else {
						let isArray = false;
						try {
							const json = typeof(result?.Value) === 'string' ? JSON.parse(result?.Value || '{}') : result?.Value;
							isArray = Array.isArray(json);

							const fields = {};
							if (isArray) {
								json.map(f => {
									Object.keys(f).map(a => {
										if (!fields.hasOwnProperty(a)) {
											fields[a] = this.getValueType(f[a]);
										}
									});
								});

								let newFields = Object.keys(fields).map(f => ({ id: f, dataType: fields[f], visible: true, caption: f }));
								let arrayFields = (this.editingOutput.output.ArrayFields || []).filter(f => newFields.some(n => n.id === f.id));
								arrayFields.push(...newFields.filter(f => !arrayFields.some(a => a.id === f.id)));

								this.$set(this.editingOutput.output, 'ArrayFields', arrayFields);
							}
						} catch {
							//
						}
						this.$set(this.editingOutput.output, 'IsArray', isArray);
						save();
					}
				} finally {
					this.isSavingOutputScript = false;
				}
			},
			addOutput() {
				this.service.JsonDefinition.Outputs.push({
					Name: null,
					Path: null,
					OutputEvalType: 1,
					DataType: 'string',
					ListOptions: [],
				});
			},
			editOutputScript(output) {
				this.editingOutput.popup = false;
				this.editingOutput.columnsPopupVisible = false;
				setImmediate(() => {
					if (this._isDestroyed) return;
					this.editingOutput.output = output;
					this.editingOutput.popup = true;
					this.editingOutput.script = output.Path;
				});
			},
			editOutputColumns(output) {
				this.editingOutput.popup = false;
				this.editingOutput.columnsPopupVisible = false;
				setImmediate(() => {
					if (this._isDestroyed) return;
					this.editingOutput.output = output;
					this.editingOutput.columnsPopupVisible = true;
				});
			},
			onOutputContextMenuItemClick(e) {
				if (e.itemData.prevent) return;
				if (__DEBUG__)
					console.debug('onOutputContextMenuItemClick', { e });

				const item = e.itemData;
				const param = item?.parameter;
				const insertText = (text) => {
					const area = this.$refs.outputScriptArea?.$el?.querySelector('textarea');
					var startPos = area.selectionStart;
					var endPos = area.selectionEnd;
					this.editingOutput.script = area.value.substring(0, startPos)
						+ text
						+ area.value.substring(endPos, area.value.length);
				}
				if (param) {
					switch(param.DataType) {
						case 'number':
						case 'boolean':
							insertText(`@${item.id}`);
							break;
						case 'date':
							insertText(`new Date('@${item.id}')`);
							break;
						default:
							insertText(`'@${item.id}'`);
							break;
					}
				} else if (item.codeSnippet) {
					insertText(item.codeSnippet);
				}
			},
			getJson(v) {
				try {
					const json = JSON.parse(v || '');
					return json;
				} catch {
					return JSON.parse((v || '').replace(new RegExp('undefined', 'g'),'null') || '{}');
				}
			},
			calculateOutputs(outputs) {
				this.isCalculatingResults = true;
				return this.$Data.postExternalApi('ExternalService/CalculateOutputs', {
					ExternalServiceOid: this.service?.Oid,
					Parameters: this.apiParams,
					Outputs: outputs || this.service.JsonDefinition.Outputs,
					JsonStringData: JSON.stringify(this.apiResponse?.__calculateOutputsResult__ || this.apiResponse?.__calculateOutputsResult || this.apiResponse),
				}, { dataType: 'text' }).then(v => {
					let r = this.getJson(v);
					this.formattedResponse = r.Data?.FormattedResponse;
					this.outputResults = r.Data?.OutputResults;
					this.outputResultsPopupVisible = false;
					this.$nextTick(() => this.outputResultsPopupVisible = true);
					return r;
				}).finally(() => this.isCalculatingResults = false);
			},
			addListOption(holder, scrollViewRef) {
				holder.push({ Key: null, Value: null });
				setImmediate(() => {
					if (this._isDestroyed) return;
					var scrollView = this.$refs[scrollViewRef][0];
					if (scrollView && scrollView.instance) {
						scrollView.instance.scrollBy(10000);
					}
				});
			},
		}
	};
</script>
<style lang='scss'>
	.web-service--outputs {
		@import "main/themes/generated/variables.base.scss";

		.output-result {
			vertical-align: top;
		}

		.iconed {
			.dx-button-content {
				padding-left: 0px !important;
			}
		}

		.jv-container .jv-code {
			padding: 0px !important;
		}
	}
</style>
